// solo/ShoppingList.jsx
// Smart shopping list widget — shows stock shortfalls vs upcoming bookings
// Sits on the Solo Dashboard or as a standalone page

import React, { useState, useEffect, useCallback } from 'react';
import api from '../axiosConfig';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  LinearProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Snackbar,
  Badge
} from '@mui/material';
import {
  ShoppingCart as CartIcon,
  Check as CheckIcon,
  Snooze as SnoozeIcon,
  Refresh as RefreshIcon,
  LocalShipping as DeliveryIcon,
  Warning as WarnIcon,
  Error as ErrorIcon,
  CheckCircle as OkIcon,
  Close as CloseIcon
} from '@mui/icons-material';

// ── Status config ─────────────────────────────────────────────
const STATUS = {
  active:   { label: 'Needs ordering', color: 'error',   icon: <ErrorIcon sx={{ fontSize: 16 }} /> },
  ordered:  { label: 'Ordered',        color: 'info',    icon: <DeliveryIcon sx={{ fontSize: 16 }} /> },
  resolved: { label: 'Well stocked',   color: 'success', icon: <OkIcon sx={{ fontSize: 16 }} /> }
};

// ── Urgency bar colour ────────────────────────────────────────
const barColor = (pct) => {
  if (pct >= 100) return 'success';
  if (pct >= 50)  return 'warning';
  return 'error';
};

// ── Single shopping list item card ───────────────────────────
const ShoppingItem = ({ item, onAction, actionLoading }) => {
  const pct = Math.min(100, Math.round((item.current_stock_grams / item.total_required_grams) * 100));
  const status = STATUS[item.status] || STATUS.active;
  const isLoading = actionLoading === item.id;

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 1.5,
        borderRadius: 2,
        opacity: item.status === 'resolved' ? 0.65 : 1,
        borderLeft: '3px solid',
        borderLeftColor:
          item.status === 'resolved' ? 'success.main' :
          item.shortfall_grams > 50   ? 'error.main'   : 'warning.main'
      }}
    >
      {/* ── Item header ── */}
      <Box sx={{ p: 1.5, pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, mr: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {item.product_name}
              </Typography>
              <Chip
                icon={status.icon}
                label={status.label}
                size="small"
                color={status.color}
                variant="outlined"
                sx={{ height: 20, fontSize: '10px', '& .MuiChip-icon': { fontSize: '12px' } }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              {item.appointment_count} appointment{item.appointment_count !== 1 ? 's' : ''}
              {item.earliest_client && ` · Next: ${item.earliest_client}`}
              {item.earliest_appointment_date && (
                ` · ${new Date(item.earliest_appointment_date).toLocaleDateString('en-GB', {
                  weekday: 'short', day: 'numeric', month: 'short'
                })}`
              )}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 500 }}
              color={item.shortfall_grams > 0 ? 'error.main' : 'success.main'}
            >
              {item.shortfall_grams > 0
                ? `${item.shortfall_grams.toFixed(0)}g short`
                : 'Sufficient'
              }
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {item.current_stock_grams.toFixed(0)}g of {item.total_required_grams.toFixed(0)}g needed
            </Typography>
          </Box>
        </Box>

        {/* ── Stock bar ── */}
        <Box sx={{ mt: 1 }}>
          <LinearProgress
            variant="determinate"
            value={pct}
            color={barColor(pct)}
            sx={{ height: 5, borderRadius: 2.5, mb: 0.5 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">
              Have: {item.current_stock_grams.toFixed(0)}g
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {pct}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Need: {item.total_required_grams.toFixed(0)}g
            </Typography>
          </Box>
        </Box>

        {/* ── Ordered delivery note ── */}
        {item.status === 'ordered' && item.expected_delivery && (
          <Alert severity="info" icon={<DeliveryIcon />} sx={{ mt: 1, py: 0.25, fontSize: '12px' }}>
            Delivery expected {new Date(item.expected_delivery).toLocaleDateString('en-GB', {
              weekday: 'short', day: 'numeric', month: 'short'
            })}
          </Alert>
        )}
      </Box>

      {/* ── Actions ── */}
      {item.status !== 'resolved' && (
        <>
          <Divider />
          <Box sx={{ px: 1.5, py: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {item.status === 'active' && (
              <>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  startIcon={isLoading ? <CircularProgress size={12} color="inherit" /> : <CartIcon />}
                  onClick={() => onAction(item.id, 'mark_ordered')}
                  disabled={isLoading}
                  sx={{ fontSize: '12px' }}
                >
                  Mark as ordered
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  startIcon={<SnoozeIcon />}
                  onClick={() => onAction(item.id, 'snooze')}
                  disabled={isLoading}
                  sx={{ fontSize: '12px', color: 'text.secondary' }}
                >
                  Remind tomorrow
                </Button>
              </>
            )}
            {item.status === 'ordered' && (
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={isLoading ? <CircularProgress size={12} color="inherit" /> : <CheckIcon />}
                onClick={() => onAction(item.id, 'mark_resolved')}
                disabled={isLoading}
                sx={{ fontSize: '12px' }}
              >
                Mark received
              </Button>
            )}
          </Box>
        </>
      )}
    </Paper>
  );
};


// ═══════════════════════════════════════════════════════════════
// ShoppingList — main component
// Props:
//   compact  — boolean, shows summary card only (for dashboard widget)
//   onBadge  — callback(count) to update parent badge
// ═══════════════════════════════════════════════════════════════
const ShoppingList = ({ compact = false, onBadge }) => {

  const [data, setData]                 = useState(null);
  const [loading, setLoading]           = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [filter, setFilter]             = useState('all');
  const [actionLoading, setActionLoading] = useState(null);
  const [snackbar, setSnackbar]         = useState({ open: false, message: '', severity: 'success' });

  // Delivery date dialog
  const [deliveryDialog, setDeliveryDialog] = useState({ open: false, itemId: null, date: '' });

  // ── Load list ─────────────────────────────────────────────
  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      const res = await api.get('/solo/shopping-list', { params });
      setData(res.data);
      if (onBadge) onBadge(res.data.summary?.active || 0);
    } catch (err) {
      console.error('Error loading shopping list:', err);
    } finally {
      setLoading(false);
    }
  }, [filter, onBadge]);

  useEffect(() => { loadList(); }, [loadList]);

  // ── Recalculate ───────────────────────────────────────────
  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      await api.post('/solo/shopping-list/recalculate');
      await loadList();
      setSnackbar({ open: true, message: 'Shopping list recalculated', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Could not recalculate', severity: 'error' });
    } finally {
      setRecalculating(false);
    }
  };

  // ── Item action handler ───────────────────────────────────
  const handleAction = async (itemId, action, extraData = {}) => {
    if (action === 'mark_ordered') {
      // Show delivery date dialog first
      setDeliveryDialog({ open: true, itemId, date: '' });
      return;
    }

    setActionLoading(itemId);
    try {
      const body = { action, ...extraData };
      if (action === 'snooze') {
        // Snooze until tomorrow
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        body.snooze_until = tomorrow.toISOString().split('T')[0];
      }

      await api.patch(`/solo/shopping-list/${itemId}`, body);
      await loadList();
      setSnackbar({
        open: true,
        message: action === 'mark_resolved' ? 'Marked as received. Stock updated.' : 'Updated.',
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({ open: true, message: 'Could not update item.', severity: 'error' });
    } finally {
      setActionLoading(null);
    }
  };

  // ── Confirm ordered with delivery date ────────────────────
  const handleConfirmOrdered = async () => {
    const { itemId, date } = deliveryDialog;
    setDeliveryDialog({ open: false, itemId: null, date: '' });
    setActionLoading(itemId);
    try {
      await api.patch(`/solo/shopping-list/${itemId}`, {
        action: 'mark_ordered',
        expected_delivery: date || null
      });
      await loadList();
      setSnackbar({ open: true, message: 'Marked as ordered.', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Could not update item.', severity: 'error' });
    } finally {
      setActionLoading(null);
    }
  };

  // ── Filter buttons ────────────────────────────────────────
  const filters = [
    { key: 'all',      label: 'All' },
    { key: 'active',   label: `Needs action${data?.summary?.active ? ` (${data.summary.active})` : ''}` },
    { key: 'ordered',  label: 'Ordered' },
    { key: 'resolved', label: 'OK' }
  ];

  const items = data?.items || [];

  // ── Compact mode — dashboard widget ──────────────────────
  if (compact) {
    const activeCount = data?.summary?.active || 0;
    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Badge badgeContent={activeCount} color="error">
              <CartIcon color="action" />
            </Badge>
            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
              Shopping list
            </Typography>
          </Box>
          <Button size="small" variant="text" href="/solo/shopping-list">
            View all
          </Button>
        </Box>

        {loading ? (
          <CircularProgress size={24} />
        ) : activeCount === 0 ? (
          <Alert severity="success" sx={{ fontSize: '12px', py: 0.5 }}>
            All products stocked for upcoming appointments.
          </Alert>
        ) : (
          <>
            <Alert severity="warning" sx={{ fontSize: '12px', py: 0.5, mb: 1 }}>
              {activeCount} product{activeCount !== 1 ? 's' : ''} need ordering before upcoming appointments.
            </Alert>
            {items.filter(i => i.status === 'active').slice(0, 3).map(item => (
              <Box key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, fontSize: '13px' }}>
                <Typography variant="body2">{item.product_name}</Typography>
                <Typography variant="body2" color="error.main">
                  {item.shortfall_grams.toFixed(0)}g short
                </Typography>
              </Box>
            ))}
          </>
        )}
      </Paper>
    );
  }

  // ── Full page mode ────────────────────────────────────────
  return (
    <Box sx={{ maxWidth: 680, mx: 'auto', p: 2 }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Shopping list
          </Typography>
          <Typography variant="body2" color="text.secondary">
            14-day window · updates when appointments are booked
          </Typography>
        </Box>
        <Tooltip title="Recalculate from current bookings and stock">
          <span>
            <IconButton onClick={handleRecalculate} disabled={recalculating}>
              {recalculating ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* ── Summary metrics ── */}
      {data?.summary && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mb: 2 }}>
          {[
            { label: 'Needs action', value: data.summary.active,   color: data.summary.active > 0 ? 'error.main' : 'text.primary' },
            { label: 'Ordered',      value: data.summary.ordered,  color: 'info.main' },
            { label: 'Well stocked', value: data.summary.resolved, color: 'success.main' }
          ].map(m => (
            <Paper key={m.label} sx={{ p: 1.5, textAlign: 'center', bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                {m.label}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 500, color: m.color }}>
                {m.value}
              </Typography>
            </Paper>
          ))}
        </Box>
      )}

      {/* ── Filter tabs ── */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {filters.map(f => (
          <Chip
            key={f.key}
            label={f.label}
            onClick={() => setFilter(f.key)}
            variant={filter === f.key ? 'filled' : 'outlined'}
            color={filter === f.key ? 'primary' : 'default'}
            size="small"
            sx={{ cursor: 'pointer' }}
          />
        ))}
      </Box>

      {/* ── Items ── */}
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={32} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Loading shopping list...
          </Typography>
        </Box>
      ) : items.length === 0 ? (
        <Alert severity={filter === 'active' ? 'success' : 'info'}>
          {filter === 'active'
            ? 'No products need ordering right now.'
            : 'No items in this view.'
          }
        </Alert>
      ) : (
        items.map(item => (
          <ShoppingItem
            key={item.id}
            item={item}
            onAction={handleAction}
            actionLoading={actionLoading}
          />
        ))
      )}

      {/* ── Empty history note ── */}
      {!loading && data?.summary?.total === 0 && (
        <Alert severity="info" sx={{ mt: 1 }}>
          The shopping list builds automatically once you start logging materials on job cards.
          After a few appointments the system will know your typical product usage per service.
        </Alert>
      )}

      {/* ── Delivery date dialog ── */}
      <Dialog open={deliveryDialog.open} onClose={() => setDeliveryDialog({ open: false, itemId: null, date: '' })} maxWidth="xs" fullWidth>
        <DialogTitle>Mark as ordered</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            When do you expect delivery? (optional)
          </Typography>
          <TextField
            label="Expected delivery date"
            type="date"
            value={deliveryDialog.date}
            onChange={e => setDeliveryDialog(prev => ({ ...prev, date: e.target.value }))}
            fullWidth
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeliveryDialog({ open: false, itemId: null, date: '' })}>
            Cancel
          </Button>
          <Button onClick={handleConfirmOrdered} variant="contained">
            Confirm ordered
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default ShoppingList;